import type { CategorieMarqueur } from '../../types/map';
import type { ResolvedBaseMap } from '../map/useBaseMap';

/**
 * Retour de parseUrlState — valeurs initiales extraites de l'URL.
 */
export interface UrlStateInitial {
  /** UUID du fond de carte (campus) extrait de ?tiles=<uuid> */
  initialBaseMapId?: string;
  /** Set de categorieId extraites de ?cat=<id,id,...> */
  initialCheckedCategorieIds: Set<number>;
}

/**
 * Parse les paramètres URL et retourne les valeurs initiales pour useUrlState.
 * Pure function (sans effets).
 *
 * **Lecture** :
 *   - `?tiles=<uuid>` → valide et retourne le UUID s'il existe dans resolvedBaseMaps
 *   - `?cat=<id,id,...>` → valide et retourne les IDs s'ils existent dans categories
 *   - Fallback silencieux : tiles inconnu → ignorer ; cat invalide → ignorer l'id
 *
 * @param resolvedBaseMaps - Fonds de carte résolus (pour validation)
 * @param categories - Catégories disponibles (pour validation)
 * @returns Valeurs initiales (avec fallback si URL invalide)
 */
export function parseUrlState(
  resolvedBaseMaps: ResolvedBaseMap[],
  categories: CategorieMarqueur[]
): UrlStateInitial {
  const params = new URLSearchParams(window.location.search);

  let initialBaseMapId: string | undefined;
  const initialCheckedCategorieIds = new Set<number>();

  // --- Param `tiles` (UUID du fond de carte) ---
  const tilesParam = params.get('tiles');
  if (tilesParam) {
    // Valider que le UUID existe dans resolvedBaseMaps
    const found = resolvedBaseMaps.find((bm) => bm.id === tilesParam);
    if (found) {
      initialBaseMapId = tilesParam;
    }
    // Sinon : fallback silencieux (utiliser le fond par défaut dans useLivingLabSearch)
  }

  // --- Param `cat` (catégories cochées : id,id,...) ---
  const catParam = params.get('cat');
  if (catParam) {
    const catIds = catParam.split(',').map((s) => parseInt(s.trim(), 10));
    const validCategoryIds = new Set(
      categories.map((c) => c.id).filter((id): id is number => id != null)
    );
    catIds.forEach((id) => {
      if (validCategoryIds.has(id)) {
        initialCheckedCategorieIds.add(id);
      }
      // Sinon : ignorer l'ID invalide (fallback silencieux)
    });
  }

  return {
    initialBaseMapId,
    initialCheckedCategorieIds,
  };
}

/**
 * Synchronise l'état actuel (fond de carte + catégories cochées) vers l'URL.
 * Utilisée par MapView dans un useEffect (SPEC §2.4, §2.7).
 *
 * @param activeBaseMapId - UUID du fond de carte actuel
 * @param checkedCategorieIds - Set des IDs de catégories cochées
 */
export function syncUrlState(
  activeBaseMapId: string | undefined,
  checkedCategorieIds: Set<number>
): void {
  const params = new URLSearchParams();

  // Param `tiles` (UUID du fond de carte)
  if (activeBaseMapId) {
    params.set('tiles', activeBaseMapId);
  }

  // Param `cat` (IDs des catégories cochées, séparées par des virgules, triées)
  if (checkedCategorieIds.size > 0) {
    const catIds = Array.from(checkedCategorieIds).sort((a, b) => a - b);
    params.set('cat', catIds.join(','));
  }

  // Construire l'URL complète
  const queryString = params.toString();
  const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

  // Synchroniser avec replaceState (pas de recharge, pas d'historique)
  window.history.replaceState(null, '', newUrl);
}
